/**
 *
 * The Axios API Helper - axios.js
 *
 * This file is where the get, post, put, and delete (del)
 * methods live. Any Vue template that needs to send
 * any of these types of requests just has to include
 * this file inside the <script> tags using the following
 * template:
 *
 * import { [method-name] } from ' [filepath to this file] '
 *
 */

import AppConfig from "./app.config";
import { logout } from "./helpers";

const axios = require("axios");

/**
 * Base API URL variable -- determined by whether or not we're serving locally
 * @type {String}
 */
let baseUrl = AppConfig.API_URL;
if (window.location.origin.indexOf(AppConfig.LOCAL_URL) > -1) {
  baseUrl = AppConfig.LOCAL_API_URL;
} else if (window.location.origin.indexOf(AppConfig.STAGING_URL) > -1) {
  baseUrl = AppConfig.STAGING_API_URL;
} else {
  baseUrl = AppConfig.PROD_API_URL;
}

axios.defaults.baseURL = baseUrl;

axios.interceptors.response.use(
  res => {
    return res;
  },
  err => {
    if (err.response.status === 401) {
      logout();
      window.location.href = "/";
    }
    return Promise.reject(err);
  }
);

/**
 * Sends an HTTP GET request
 * @param  {String} url          The API endpoint
 * @param  {String} [token=null] The local bearer token (defaults to null)
 */
export function get(url, token = null) {
  const requestUrl = `/api/${url}`;
  return axios({
    method: "GET",
    url: requestUrl,
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
}

/**
 * Sends an HTTP POST request
 * @param  {String} url             The API endpoint
 * @param  {Object} payload         The object to be POSTed to the server
 * @param  {String} [token=null]    The local bearer token (defaults to null)
 * @param  {Boolean} [isMultiPart=false] Sets Content-Type to multipart/form-data
 */
export function post(url, payload, token = null, isMultiPart = false) {
  const requestUrl = `/api/${url}`;
  let headers = {
    Authorization: `Bearer ${token}`
  };
  if (isMultiPart) {
    headers["Content-Type"] = "multipart/form-data";
  }

  return axios({
    method: "POST",
    url: requestUrl,
    data: payload,
    headers
  });
}

/**
 * Sends an HTTP DELETE request
 * @param  {String} url          The API endpoint
 * @param  {String} [token=null] The local bearer token (defaults to null)
 */
export function del(url, token = null) {
  const requestUrl = `/api/${url}`;
  return axios({
    method: "DELETE",
    url: requestUrl,
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
}

/**
 * Sends an HTTP PUT request
 * @param  {String} url          The API endpoint
 * @param  {Object} payload      The object to be PUT to the server
 * @param  {String} [token=null] The local bearer token (defaults to null)
 */
export function put(url, payload, token = null) {
  const requestUrl = `/api/${url}`;
  return axios({
    method: "PUT",
    url: requestUrl,
    data: payload,
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
}

/**
 * Sends an HTTP PATCH request
 * @param  {String} url          The API endpoint
 * @param  {Object} payload      The object to be PUT to the server
 * @param  {String} [token=null] The local bearer token (defaults to null)
 */
export function patch(url, payload, token = null) {
  const requestUrl = `/api/${url}`;
  return axios({
    method: "PATCH",
    url: requestUrl,
    data: payload,
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
}
